import React, { useEffect, useRef, useState } from 'react';
import { useCompaniesPage } from '../../hooks/useOrgQueries';
import { LedgerSelect, LedgerSelectOption } from '../ui';
import { cn } from '../../lib/cn';

// No team select here — team filtering arrives only via the cross-nav URL
// param (`?team=`), which the page owns.
export interface DepartmentViewsProps {
  search: string;
  company: string | 'ALL';
  activeOnly: boolean;
  onSearchChange: (search: string) => void;
  onCompanyChange: (company: string | 'ALL') => void;
  onActiveOnlyChange: (activeOnly: boolean) => void;
}

export const DepartmentViews: React.FC<DepartmentViewsProps> = (props) => {
  const [term, setTerm] = useState(props.search);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastEmitted = useRef(props.search);
  // First page only — the select caps at the server page size, acceptable for org-scale data.
  const companies = useCompaniesPage({ search: '', page: 1 });

  useEffect(() => () => clearTimeout(debounce.current), []);

  // Sync external search changes into the input, but ignore the parent echoing
  // back a value this input just emitted so in-progress typing is never clobbered.
  useEffect(() => {
    if (props.search !== lastEmitted.current) {
      clearTimeout(debounce.current);
      setTerm(props.search);
      lastEmitted.current = props.search;
    }
  }, [props.search]);

  const companyOptions: LedgerSelectOption[] = [
    { value: 'ALL', label: 'All companies' },
    ...(companies.data?.results ?? []).map((company) => ({ value: company.id, label: company.name })),
  ];

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-y-2 pt-2">
      <div className="flex flex-wrap items-center gap-4">
        <LedgerSelect
          value={props.company}
          options={companyOptions}
          onValueChange={(value) => props.onCompanyChange(value)}
          ariaLabel="Filter by company"
        />
        <button
          type="button"
          role="switch"
          aria-checked={props.activeOnly}
          onClick={() => props.onActiveOnlyChange(!props.activeOnly)}
          className="flex cursor-pointer items-center gap-2"
        >
          <span aria-hidden className={cn('h-3 w-3 border border-ink', props.activeOnly && 'bg-ink')} />
          <span className="text-scale-1 text-ink">Active only</span>
        </button>
      </div>
      <input
        value={term}
        placeholder="Search departments…"
        onChange={(event) => {
          const { value } = event.target;
          setTerm(value);
          clearTimeout(debounce.current);
          debounce.current = setTimeout(() => {
            lastEmitted.current = value;
            props.onSearchChange(value);
          }, 300);
        }}
        className="w-44 border-b border-rule-faint bg-transparent text-scale-1 text-ink outline-none placeholder:text-ink-muted/60 focus:border-accent"
        aria-label="Search departments"
      />
    </div>
  );
};
